/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from 'react';
import {Button, Divider, Form, Input, Modal, Radio, Select} from "antd";
import {genMessage} from "../genMessage";
import groupAPI from "../../../api/groupAPI";
import userAPI from "../../../api/userAPI";
import fcmMessageAPI from "../../../api/fcmMessageAPI";
import {useAppStore} from "../../../stores/appStore";

const formItemLayout = {
	labelCol: {
		xs: { span: 24 },
		sm: { span: 4 },  // 레이블의 폭 조정 (기본 8을 줄여봄)
	},
	wrapperCol: {
		xs: { span: 24 },
		sm: { span: 18 },  // 입력 필드의 폭 조정
	},
};

// ========================================================================================================== //

interface PopupProps {
	showPopup: boolean;
	onCancel: () => any;
	onSend: () => any;
}

const MessageSendPopup = ({
	                          showPopup,
	                          onCancel,
	                          onSend,
                          }: PopupProps) => {
	const setShouldShowSpinner = useAppStore((state) => state.setShouldShowSpinner);
	const [receiversLabelString, setReceiversLabelString] = useState('그룹 ID');
	const [selectedType, setSelectedType] = useState<'group' | 'member' | 'user'>('group');
	const [firstOptions, setFirstOptions] = useState<{ value: string, label: string }[]>([]);
	const [secondOptions, setSecondOptions] = useState<{ value: string, label: string }[]>([]);
	const [disableButton, setDisableButton] = useState(true);

	const [form] = Form.useForm();

	const isValidJSON = (input: string) => {
		try {
			if (input) JSON.parse(input);
			return true;
		} catch {
			return false;
		}
	};

	const validateForm = (changedValues: any, allValues: any) => {
		const { receivers = [], data = '' } = allValues;

		const isReceiversValid = receivers.length > 0;
		const isDataValid = isValidJSON(data);

		setDisableButton(!isReceiversValid || !isDataValid);
	};

	const onSendNow = async () => {
		const values = form.getFieldsValue();

		try {
			setShouldShowSpinner(true);
			if (values.data) {
				values.data = JSON.parse(values.data);
			}
		} catch (error) {
			console.error('Invalid JSON format in "data" field:', error);
			Modal.error({
				title: 'Invalid JSON',
				content: '데이터 항목에 입력된 내용은 올바른 JSON 형식이어야 합니다.',
			});
			return;
		} finally {
			setShouldShowSpinner(false);
		}

		if (values.receivers.length === 0) return;

		const messages = genMessage(values);
		await fcmMessageAPI.sendMessageAPI(messages);
		onSend();
	};

	const onClickType = (e: any) => {
		const value = e.target.value;
		setSelectedType(value);
		if (value !== selectedType) {
			form.setFieldsValue({ receivers: [] });
		}
		if (value === 'group') setReceiversLabelString('그룹명');
		if (value === 'member') setReceiversLabelString('멤버 닉네임');
		if (value === 'user') setReceiversLabelString('유저명');
	};

	const onSelectGroupOnMemberType = async (groupId: any) => {
		const res = await groupAPI.fetchGroupMemberList(groupId);
		const newOptions = res.map((item: any) => ({ value: item.id, label: item.nickname }));
		setSecondOptions(newOptions);
	};

	useEffect(() => {
		if (selectedType === 'group' || selectedType === 'member') {
			groupAPI.fetchGroupList().then((res) => {
				const newOptions = res.map((item: any) => ({ value: item.id, label: item.name }));
				setFirstOptions(newOptions);
			});
		}
		if (selectedType === 'user') {
			userAPI.getUserListAPI().then((res) => {
				const newOptions = res.map((item: any) => ({ value: item.id, label: item.name }));
				setFirstOptions(newOptions);
			});
		}
	}, [selectedType]);

	return (
		<Modal
			title={<span style={{ color: '#1677ff', fontSize: '1em' }}>FCM 메시지 발송</span>}
			open={showPopup}
			onCancel={onCancel}
			footer={null}
			width={700}
			styles={{ body: { paddingBottom: 0 } }}
		>
			<Divider />
			<Form
				{...formItemLayout}
				form={form}
				variant={'outlined'}
				size={'large'}
				style={{ width: '100%', marginTop: '20px' }}
				onValuesChange={validateForm}
			>
				<Form.Item label="제목" name="title">
					<Input placeholder="제목을 입력해주세요" />
				</Form.Item>

				<Form.Item label="본문" name="body">
					<Input placeholder="본문을 입력해주세요" />
				</Form.Item>

				<Form.Item label="데이터" name="data">
					<Input.TextArea
						rows={3}
						placeholder='JSON 형식으로 입력해주세요. 예: {"key": "value"}'
					/>
				</Form.Item>

				<Form.Item label="우선순위" name="prioity" initialValue="normal">
					<Radio.Group buttonStyle="solid">
						<Radio.Button value="normal">보통</Radio.Button>
						<Radio.Button value="high">높음</Radio.Button>
					</Radio.Group>
				</Form.Item>

				<Form.Item label="유형" name="type" initialValue="group">
					<Radio.Group buttonStyle="solid" onChange={onClickType}>
						<Radio value="group">그룹</Radio>
						<Radio value="member">멤버</Radio>
						<Radio value="user">유저</Radio>
					</Radio.Group>
				</Form.Item>

				{(selectedType === 'group' || selectedType === 'user') && (
					<Form.Item label={receiversLabelString} name="receivers">
						<Select
							mode="multiple"
							options={firstOptions}
							filterOption={(inputValue, option) =>
								(option?.value as string).toLowerCase().includes(inputValue.toLowerCase()) ||
								(option?.label as string).toLowerCase().includes(inputValue.toLowerCase())
							}
						/>
					</Form.Item>
				)}

				{selectedType === 'member' && (
					<Form.Item label={receiversLabelString}>
						<Form.Item noStyle>
							<Select
								showSearch
								options={firstOptions}
								onChange={onSelectGroupOnMemberType}
								filterOption={(inputValue, option) =>
									(option?.value as string).toLowerCase().includes(inputValue.toLowerCase()) ||
									(option?.label as string).toLowerCase().includes(inputValue.toLowerCase())
								}
							/>
						</Form.Item>
						<div style={{ height: '15px' }} />
						<Form.Item noStyle name="receivers">
							<Select
								mode="multiple"
								options={secondOptions}
								filterOption={(inputValue, option) =>
									(option?.value as string).toLowerCase().includes(inputValue.toLowerCase()) ||
									(option?.label as string).toLowerCase().includes(inputValue.toLowerCase())
								}
							/>
						</Form.Item>
					</Form.Item>
				)}

				<Form.Item style={{display: 'flex', justifyContent: 'flex-end', marginRight: '50px'}} label={null}>
					<Button
						onClick={onSendNow}
						type="primary"
						size="middle"
						htmlType="submit"
						disabled={disableButton}
					>
						즉시 전송
				</Button>
				</Form.Item>
			</Form>
		</Modal>
	);
};

export default MessageSendPopup;